import TooltipIcon from '@repo/ui/src/components/Icons/TooltipIcon';
import { Tooltip } from '@repo/ui/src/components/Tooltip';
import { classNames } from '../../utils/classNames';
import { formatTemp } from './_buildSchedule';
import { PickerButtonGroup, PickerSection } from './_PickerSection';
import type { Method, PlanPanelLabels } from './types';

interface MethodPickerProps {
  label: string;
  methods: Array<Method>;
  selectedIndex: number;
  tierKey: string;
  onSelect: (index: number) => void;
  theme: 'dark' | 'light';
  labels?: PlanPanelLabels;
}

function getDescription(method: Method, tierKey: string, labels?: PlanPanelLabels): string {
  if (method.shortDescription) return method.shortDescription;
  const ct = method.cookTimes?.find((c) => c.tier === tierKey);
  if (!ct) return '';
  const temp = formatTemp(method.tempF, method.tempFMax, labels);
  const timeH = ct.cookMinutesMin / 60;
  const timeMaxH = ct.cookMinutesMax ? ct.cookMinutesMax / 60 : undefined;
  const hrsLabel = labels?.descriptionHoursLabel ?? 'hrs';
  const forLabel = labels?.descriptionForLabel ?? 'for';
  const time = timeMaxH ? `${timeH}-${timeMaxH} ${hrsLabel}` : `${timeH} ${hrsLabel}`;
  return `${temp} ${forLabel} ${time}`;
}

function MethodLabel({ method, tierKey, isDark, labels }: { method: Method; tierKey: string; isDark: boolean; labels?: PlanPanelLabels }) {
  const description = getDescription(method, tierKey, labels);

  return (
    <div className='flex items-center justify-between w-full gap-2'>
      <span className="flex items-center gap-1">
        <span>{method.name}</span>
        {method.tooltip && (
          <Tooltip text={method.tooltip} className='-mt-1'>
            <TooltipIcon className={isDark ? 'text-neutral-200' : 'text-neutral-900'} color="currentColor" />
          </Tooltip>
        )}
      </span>
      {description && (
        <span
          className={classNames(
            'tra-text-web-body-l normal-case',
            isDark ? 'text-neutral-100 print:text-neutral-600' : 'text-neutral-600',
          )}
        >
          {description}
        </span>
      )}
    </div>
  );
}

export function MethodPicker({
  label,
  methods,
  selectedIndex,
  tierKey,
  onSelect,
  theme,
  labels,
}: MethodPickerProps) {
  const isDark = theme === 'dark';

  return (
    <PickerSection label={label} theme={theme}>
      <PickerButtonGroup
        direction="column"
        theme={theme}
        options={methods.map((method, i) => ({
          key: method.slug,
          label: <MethodLabel method={method} tierKey={tierKey} isDark={isDark} labels={labels} />,
          isSelected: i === selectedIndex,
          onSelect: () => onSelect(i),
        }))}
      />
    </PickerSection>
  );
}
